import { useState, useCallback, forwardRef } from 'react';

import type { UserDropdownTriggerProps } from './UserDropdown.types';

import { Avatar } from '@/components/ui/Avatar';

/**
 * Avatar button that triggers the user dropdown.
 * Shows avatar image if available, otherwise the username's initials.
 * On mobile, displays a pulse animation on tap.
 */
export const UserDropdownTrigger = forwardRef<
  HTMLButtonElement,
  UserDropdownTriggerProps
>(function UserDropdownTrigger(
  { avatar, username, isOpen, onClick, isMobile = false },
  ref
) {
  const [isPulsing, setIsPulsing] = useState(false);

  const handleClick = useCallback(() => {
    if (isMobile && !isOpen) {
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), 400);
    }
    onClick();
  }, [isMobile, isOpen, onClick]);

  return (
    <button
      ref={ref}
      type="button"
      className="
        flex items-center justify-center
        w-10 h-10 p-0
        border-none bg-transparent
        rounded-lg cursor-pointer
        transition-colors duration-150 ease-out
        hover:bg-lift-3
        focus:outline-none focus-visible:ring-state-focus
      "
      aria-label="User menu"
      aria-expanded={isOpen}
      aria-haspopup="menu"
      onClick={handleClick}
    >
      <Avatar
        name={username}
        src={avatar}
        size="sm"
        tone="accent"
        className={isPulsing ? 'animate-avatar-pulse' : ''}
      />
    </button>
  );
});

export default UserDropdownTrigger;
